import React, { useState } from 'react'
import Stepper from '@material-ui/core/Stepper'
import Step from '@material-ui/core/Step'
import StepLabel from '@material-ui/core/StepLabel'
import StepContent from '@material-ui/core/StepContent'
import Button from '@material-ui/core/Button'

import CustomerAuthForm from './components/customer/CustomerAuthForm'
import CustomerInformation from './components/customer/CustomerInformation'
import CustomerConfigForm from './components/customer/CustomerConfigForm'
import provisioningService from './services/Provisioning'
import ErrorBoundary from '../shared/components/DefaultErrorBoundary'

function ProvisioningWizard() {
  const [activeStep, setActiveStep] = useState(0)
  const [customerInfo, setCustomerInfo] = useState({})
  const [downloadUrl, setDownloadUrl] = useState('')
  const [components, setComponents] = useState([])

  async function submitCustomerInfo(values, { setSubmitting, setStatus }) {
    let componentConfigFormData
    let customerInfo
    let response

    try {
      response = await provisioningService.getCustomerProvisions(
        values.customerId,
        values.customerCode,
      )
      if (!response.products) {
        console.error('Invalid customer id and/or code.')
        setSubmitting(false)
        setStatus({
          error: true,
          message: ['Invalid customer and/or code.'],
        })
        return
      }
    } catch (error) {
      console.error(error)
      setSubmitting(false)
      setStatus({
        error: true,
        message: [
          'There was an issue with the request. Please try again. If the issue persists, please contact support.',
        ],
      })
      return
    }

    try {
      console.log('Success data from customer auth form:', response)
      customerInfo = {
        Name: response.customer.Name,
        CustomerSAPID: response.customer.CustomerID,
        Region: response.customer.Region,
        ProductType: response.products[0].ProductName,
        DeviceCount: response.products.length,
        Code: response.products[0].ProductCustomerCode,
      }

      componentConfigFormData = markConfigurationsAsConfigurable(response.products)

      for (const componentIndex in componentConfigFormData) {
        for (const parameterIndex in componentConfigFormData[componentIndex].DocumentParameters) {
          const documentParameter =
            componentConfigFormData[componentIndex].DocumentParameters[parameterIndex]
          if (
            documentParameter.DefaultValue != null &&
            documentParameter.DefaultValue.trim() !== ''
          ) {
            documentParameter.value =
              componentConfigFormData[componentIndex].DocumentParameters[
                parameterIndex
              ].DefaultValue
          }
        }
      }
    } catch (error) {
      console.error(error)
      setSubmitting(false)
      setStatus({
        error: true,
        message: [
          'There was an issue with the request. Please try again. If the issue persists, please contact support.',
        ],
      })
      return
    }

    try {
      setComponents(componentConfigFormData)
      setCustomerInfo(customerInfo)
      setActiveStep(activeStep + 1)
    } catch (error) {
      console.error(error)
      setSubmitting(false)
      setStatus({ error: true, message: error.message })
    }
  }

  const markConfigurationsAsConfigurable = (components) => {
    // iterate over the component table data
    for (const component of components) {
      // determine if the component has configurable parameters
      let hasConfigurableParameters = false
      for (const parameter of component.DocumentParameters) {
        if (parameter.Description) {
          // adding this to handle if this is not one of our documents and doesn't have configurable parameters defined in the description
          let editable = JSON.parse(parameter.Description).editable
          let external = JSON.parse(parameter.Description).external
          if (editable && external) {
            hasConfigurableParameters = true
          }
        }
      }
      component.hasConfigurableParameters = hasConfigurableParameters
    }
    return components
  }

  async function submitUserInfo(values, { setSubmitting, setStatus }) {
    try {
      setStatus({
        message: 'Generating bundle. This make take a few seconds...',
      })

      const response = await provisioningService.createCustomerProvision(
        customerInfo.CustomerSAPID,
        customerInfo.Code,
      )
      setStatus({ message: 'Done!' })
      console.log('Success data from submitting user info:', response)

      setActiveStep(activeStep + 1)
      if (response.url != null) {
        setDownloadUrl(response.url)
      }
    } catch (error) {
      setSubmitting(false)
      setStatus({ error: true, message: error.message })
      console.error(error)
    }
  }

  return (
    <ErrorBoundary>
      <Stepper activeStep={activeStep} orientation="vertical">
        <Step>
          <StepLabel>Customer Information</StepLabel>
          <StepContent>
            <CustomerAuthForm submitCustomerInfo={submitCustomerInfo} />
          </StepContent>
        </Step>

        <Step>
          <StepLabel>User Information</StepLabel>
          <StepContent>
            <CustomerInformation {...customerInfo} />
            <CustomerConfigForm submitUserInfo={submitUserInfo} components={components} />
          </StepContent>
        </Step>

        <Step>
          <StepLabel>Download Package</StepLabel>
          <StepContent style={{ textAlign: 'center' }}>
            <Button size="large" color="secondary" variant="contained" href={downloadUrl}>
              Download
            </Button>
          </StepContent>
        </Step>
      </Stepper>
    </ErrorBoundary>
  )
}

export default ProvisioningWizard
